import { computed, Injectable, signal } from "@angular/core";

export enum PlaceTourScenario {
  None = "none",
  One = "one", // Grenoble
  Full = "full",
  Test = "test", // exhaustive data, for test only
}

export enum MeterTourScenario {
  None = "none",
  One = "one",
  Full = "full",
  Test = "test", // exhaustive data, for test only
}

export enum ShowerheadTourScenario {
  None = "none",
  One = "one",
  Full = "full",
  Test = "test", // exhaustive data, for test only
}

export enum GatewayTourScenario {
  None = "none",
  One = "one",
  Full = "full",
  Test = "test", // exhaustive data, for test only
}

export enum AlertTourScenario {
  None = "none",
  One = "one",
  Full = "full",
  Test = "test", // exhaustive data, for test only
}

export enum AppScreenMode {
  Normal = "normal",
  Loading = "loading",
}
@Injectable({
  providedIn: "root",
})
export class AppStore {
  public screenMode: AppScreenMode = AppScreenMode.Normal;

  // ---------------------
  // --- main menu
  // ---------------------
  private readonly sideMenuReduced = signal<boolean>(false); // state of main menu in desktopMode (reduced / full)
  readonly sideMenuReduced$ = this.sideMenuReduced.asReadonly();
  readonly sideNavMode$ = computed(() => (this.mobileMode() ? "over" : "side"));

  updateSideMenuState(reduced: boolean): void {
    if (reduced !== this.sideMenuReduced()) {
      this.sideMenuReduced.set(reduced);
    }
  }

  private readonly mainMenuOpened = signal<boolean>(false); // state of main menu in mobileMode (hidden / visible)
  readonly mainMenuOpened$ = this.mainMenuOpened.asReadonly();

  updateMainMenuState(opened: boolean): void {
    if (opened !== this.mainMenuOpened()) {
      this.mainMenuOpened.set(opened);
    }
  }

  // ---------------------
  // --- mobile mode
  // ---------------------

  private readonly mobileMode = signal<boolean>(false);
  readonly mobileMode$ = this.mobileMode.asReadonly();

  updateMobileMode(enabled: boolean): void {
    if (enabled !== this.mobileMode()) {
      this.mobileMode.set(enabled);
    }
  }

  // ---------------------
  // --- global loader (background queries)
  // ---------------------

  private readonly currentQueriesCount = signal<number>(0);
  readonly loading$ = computed(() => this.currentQueriesCount() > 0);

  beginQuery(): void {
    this.currentQueriesCount.set(this.currentQueriesCount() + 1);
  }

  endQuery(): void {
    if (this.currentQueriesCount() > 0) {
      this.currentQueriesCount.set(this.currentQueriesCount() - 1);
    }
  }

  // ---------------------
  // --- admin mode
  // ---------------------

  private readonly adminMode = signal<boolean>(false);
  readonly adminMode$ = this.adminMode.asReadonly();

  // ---------------------
  // --- tour mode
  // ---------------------

  private readonly tourMode = signal<boolean>(false);
  readonly tourMode$ = this.tourMode.asReadonly();

  private readonly tourSwitch = signal<boolean>(false);
  readonly tourSwitch$ = this.tourSwitch.asReadonly();

  // TEST ONLY
  private readonly tourForced = signal<boolean>(false);
  readonly tourForced$ = this.tourForced.asReadonly();

  public placesScenario: PlaceTourScenario = PlaceTourScenario.Full;
  public showerheadsScenario: ShowerheadTourScenario =
    ShowerheadTourScenario.Full;
  public metersScenario: MeterTourScenario = MeterTourScenario.Full;
  public gatewaysScenario: GatewayTourScenario = GatewayTourScenario.Full;
  public alertsScenario: AlertTourScenario = AlertTourScenario.Full;

  updateTourMode(enabled: boolean): void {
    if (enabled !== this.tourMode()) {
      this.tourMode.set(enabled);
    }
  }

  resetTourForced() {
    if (this.tourForced() === true) {
      this.tourForced.set(false);
    }
  }

  // ---------------------
  // --- special cases (TEST ONLY)
  // ---------------------

  private readonly specialCases = signal<boolean>(false);
  readonly specialCases$ = this.specialCases.asReadonly();
  private readonly canViewPlaces = signal<boolean>(false);
  readonly canViewPlaces$ = this.canViewPlaces.asReadonly();

  // ---------------------
  // --- beta features
  // ---------------------

  public readonly enableWithoutPlace = signal<boolean>(true);

  // ---------------------
  // --- global overlay
  // ---------------------

  private readonly overlayOpened = signal<boolean>(false);
  readonly overlayOpened$ = this.overlayOpened.asReadonly();

  updateOverlayState(opened: boolean): void {
    if (opened !== this.overlayOpened()) {
      this.overlayOpened.set(opened);
    }
  }
}
